import clsx from 'clsx';
import type { ReactNode } from 'react';

type OverlayWidgetProps = {
  /** Additional CSS classes for the widget container */
  className?: string;
  /** Content to render inside the widget */
  children: ReactNode;
};

/* -------------------------------------------------------------------------- */
/*                               Main component                               */
/* -------------------------------------------------------------------------- */

/**
 * A floating overlay widget container for SVG-based controls, positioned in the top-right corner of the canvas
 * The widget will automatically shift left when a details sheet is open to avoid overlap.
 * The `className` prop can be used to apply additional styling or positioning as needed.
 *
 * @example
 * ```tsx
 *   <OverlayWidget>
 *     <LayerControlPopover ... />
 *     <BlastOpacityControlLayer ... />
 *   </OverlayWidget>
 * ```
 */
const OverlayWidget = ({ className, children }: OverlayWidgetProps) => {
  return (
    <div
      className={clsx(
        'absolute inset-e-4 top-4 flex flex-col items-start gap-1',
        'group-[&:has([data-slot=sheet-content][data-open=true])]/main:inset-e-[calc(16px+var(--sheet-width))]',
        'transition-all duration-200 ease-in-out',
        className,
      )}
    >
      {children}
    </div>
  );
};
OverlayWidget.displayName = 'OverlayWidget';

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { OverlayWidget };
